import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Car, LayoutDashboard, FileText, LogOut, Users, Building, Monitor, Store, 
  BarChart3, Plus, Search, DollarSign, Briefcase, Check, X, Eye, Play, Pause,
  Calendar, Target, Clock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCampaigns() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [sites, setSites] = useState([]);
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [pendingCount, setPendingCount] = useState(0);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [formData, setFormData] = useState({
    advertiser_id: "",
    name: "",
    description: "",
    campaign_type: "standard",
    target_type: "site",
    target_ids: [],
    start_date: "",
    end_date: "",
    pricing_model: "flat_monthly",
    flat_rate: "",
    total_budget: ""
  });

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchCampaigns();
      fetchAdvertisers();
      fetchSites();
      fetchScreens();
      fetchPendingCount();
    }
  }, [user, statusFilter]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch {
      navigate("/admin/login");
    }
  };

  const fetchCampaigns = async () => {
    try {
      let url = `${API}/campaigns?limit=100`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      const response = await axios.get(url, { withCredentials: true });
      setCampaigns(response.data);
    } catch (error) {
      toast.error("Failed to fetch campaigns");
    } finally {
      setLoading(false);
    }
  };

  const fetchAdvertisers = async () => {
    try {
      const response = await axios.get(`${API}/advertisers?status=active`, { withCredentials: true });
      setAdvertisers(response.data);
    } catch {}
  };

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/sites`, { withCredentials: true });
      setSites(response.data);
    } catch {}
  };

  const fetchScreens = async () => {
    try {
      const response = await axios.get(`${API}/screens?is_active=true`, { withCredentials: true });
      setScreens(response.data);
    } catch {}
  };

  const fetchPendingCount = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/pending/count`, { withCredentials: true });
      setPendingCount(response.data.pending_approvals);
    } catch {}
  };

  const handleCreate = async () => {
    try {
      const payload = {
        advertiser_id: formData.advertiser_id,
        name: formData.name,
        description: formData.description,
        campaign_type: formData.campaign_type,
        target: {
          target_type: formData.target_type,
          site_ids: formData.target_type === "site" ? formData.target_ids : [],
          screen_ids: formData.target_type === "screen" ? formData.target_ids : [],
          screen_types: []
        },
        schedule: {
          start_date: formData.start_date,
          end_date: formData.end_date,
          time_slots: [],
          days_of_week: [0, 1, 2, 3, 4, 5, 6]
        },
        pricing: {
          model: formData.pricing_model,
          flat_rate: formData.pricing_model === "flat_monthly" ? parseFloat(formData.flat_rate) || 0 : null,
          total_budget: parseFloat(formData.total_budget) || null
        },
        content_ids: []
      };
      
      await axios.post(`${API}/campaigns`, payload, { withCredentials: true });
      toast.success("Campaign created successfully");
      setIsCreateOpen(false);
      resetForm();
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create campaign");
    }
  };

  const resetForm = () => {
    setFormData({
      advertiser_id: "", name: "", description: "", campaign_type: "standard",
      target_type: "site", target_ids: [], start_date: "", end_date: "",
      pricing_model: "flat_monthly", flat_rate: "", total_budget: ""
    });
  };

  const handleApprove = async () => {
    try {
      await axios.post(`${API}/campaigns/${selectedCampaign.campaign_id}/approve`, 
        { notes: "" }, 
        { withCredentials: true }
      );
      toast.success("Campaign approved");
      setIsApproveOpen(false);
      setSelectedCampaign(null);
      fetchCampaigns();
      fetchPendingCount();
    } catch (error) {
      toast.error("Failed to approve campaign");
    }
  };

  const handleReject = async (reason) => {
    try {
      await axios.post(`${API}/campaigns/${selectedCampaign.campaign_id}/reject`, 
        { reason }, 
        { withCredentials: true }
      );
      toast.success("Campaign rejected");
      setIsApproveOpen(false);
      setSelectedCampaign(null);
      fetchCampaigns();
      fetchPendingCount();
    } catch (error) {
      toast.error("Failed to reject campaign");
    }
  };

  const handleActivate = async (campaignId) => {
    try {
      await axios.post(`${API}/campaigns/${campaignId}/activate`, {}, { withCredentials: true });
      toast.success("Campaign activated");
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to activate");
    }
  };

  const handlePause = async (campaignId) => {
    try {
      await axios.post(`${API}/campaigns/${campaignId}/pause`, {}, { withCredentials: true });
      toast.success("Campaign paused");
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to pause campaign");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    navigate("/admin/login");
  };

  const statusColors = {
    draft: "bg-slate-500/20 text-slate-400",
    pending_approval: "bg-amber-500/20 text-amber-400",
    approved: "bg-blue-500/20 text-blue-400",
    rejected: "bg-red-500/20 text-red-400",
    active: "bg-emerald-500/20 text-emerald-400",
    paused: "bg-orange-500/20 text-orange-400",
    completed: "bg-purple-500/20 text-purple-400",
    cancelled: "bg-slate-500/20 text-slate-400"
  };

  const campaignTypeLabels = {
    standard: "Standard",
    takeover_full: "Full Takeover",
    takeover_wrapper: "Branded Wrapper",
    takeover_timeslot: "Time-slot Takeover"
  };

  return (
    <div className="admin-container min-h-screen" data-testid="admin-campaigns">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 p-6 overflow-y-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>ParkWise</span>
        </div>

        <nav className="space-y-2">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <LayoutDashboard className="w-5 h-5" /><span>Dashboard</span>
          </Link>
          <Link to="/admin/registrations" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <FileText className="w-5 h-5" /><span>Registrations</span>
          </Link>
          <Link to="/admin/sites" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Building className="w-5 h-5" /><span>Sites & Kiosks</span>
          </Link>
          <Link to="/admin/retailers" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Store className="w-5 h-5" /><span>Retailers</span>
          </Link>
          
          {/* Revenue Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Revenue</p>
            <Link to="/admin/advertisers" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Briefcase className="w-5 h-5" /><span>Advertisers</span>
            </Link>
            <Link to="/admin/campaigns" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 text-white">
              <BarChart3 className="w-5 h-5" /><span>Campaigns</span>
              {pendingCount > 0 && (
                <span className="ml-auto bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">{pendingCount}</span>
              )}
            </Link>
            <Link to="/admin/billing" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <DollarSign className="w-5 h-5" /><span>Billing</span>
            </Link>
            <Link to="/admin/revenue" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <BarChart3 className="w-5 h-5" /><span>Revenue Dashboard</span>
            </Link>
          </div>

          {/* Signage CMS Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Signage CMS</p>
            <Link to="/admin/screens" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Monitor className="w-5 h-5" /><span>Screens</span>
            </Link>
            <Link to="/admin/content" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <FileText className="w-5 h-5" /><span>Content</span>
            </Link>
            <Link to="/admin/playlists" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <LayoutDashboard className="w-5 h-5" /><span>Playlists</span>
            </Link>
          </div>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full">
            <LogOut className="w-4 h-4" /><span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Campaigns</h1>
            <p className="text-slate-400">Manage advertising campaigns and approvals</p>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-campaign-btn">
                <Plus className="w-4 h-4 mr-2" />New Campaign
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Campaign</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Advertiser *</Label>
                  <Select value={formData.advertiser_id} onValueChange={(v) => setFormData({...formData, advertiser_id: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue placeholder="Select advertiser" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      {advertisers.map(adv => (
                        <SelectItem key={adv.advertiser_id} value={adv.advertiser_id}>{adv.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Campaign Name *</Label>
                  <Input 
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                    data-testid="campaign-name-input"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input 
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <Label>Campaign Type</Label>
                  <Select value={formData.campaign_type} onValueChange={(v) => setFormData({...formData, campaign_type: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="standard">Standard Rotation</SelectItem>
                      <SelectItem value="takeover_full">Full Takeover</SelectItem>
                      <SelectItem value="takeover_wrapper">Branded Wrapper</SelectItem>
                      <SelectItem value="takeover_timeslot">Time-slot Takeover</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Target Type</Label>
                    <Select value={formData.target_type} onValueChange={(v) => setFormData({...formData, target_type: v, target_ids: []})}>
                      <SelectTrigger className="bg-slate-800 border-slate-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        <SelectItem value="site">By Site</SelectItem>
                        <SelectItem value="screen">By Screen</SelectItem>
                        <SelectItem value="network">Network-wide</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.target_type !== "network" && (
                    <div>
                      <Label>{formData.target_type === "site" ? "Sites" : "Screens"}</Label>
                      <Select 
                        value={formData.target_ids[0] || ""} 
                        onValueChange={(v) => setFormData({...formData, target_ids: [v]})}
                      >
                        <SelectTrigger className="bg-slate-800 border-slate-700">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          {formData.target_type === "site" 
                            ? sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)
                            : screens.map(s => <SelectItem key={s.screen_id} value={s.screen_id}>{s.name}</SelectItem>)
                          }
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Date *</Label>
                    <Input 
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div>
                    <Label>End Date *</Label>
                    <Input 
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Pricing Model</Label>
                    <Select value={formData.pricing_model} onValueChange={(v) => setFormData({...formData, pricing_model: v})}>
                      <SelectTrigger className="bg-slate-800 border-slate-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        <SelectItem value="flat_monthly">Flat Monthly</SelectItem>
                        <SelectItem value="cpm">CPM</SelectItem>
                        <SelectItem value="slot_ownership">Slot Ownership</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{formData.pricing_model === "flat_monthly" ? "Monthly Rate (£)" : "Total Budget (£)"}</Label>
                    <Input 
                      type="number"
                      value={formData.pricing_model === "flat_monthly" ? formData.flat_rate : formData.total_budget}
                      onChange={(e) => setFormData({
                        ...formData, 
                        [formData.pricing_model === "flat_monthly" ? "flat_rate" : "total_budget"]: e.target.value
                      })}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>
                <Button onClick={handleCreate} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-campaign-btn">
                  Create Campaign
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending_approval">Pending Approval</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          {pendingCount > 0 && (
            <Button 
              variant="outline" 
              className="border-amber-500 text-amber-400"
              onClick={() => setStatusFilter("pending_approval")}
            >
              {pendingCount} Pending Approval
            </Button>
          )}
        </div>

        {/* Campaigns List */}
        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <Card key={campaign.campaign_id} className="bg-slate-800/50 border-slate-700" data-testid={`campaign-${campaign.campaign_id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{campaign.name}</h3>
                      <Badge className={statusColors[campaign.status]}>{campaign.status.replace("_", " ")}</Badge>
                      <Badge className="bg-blue-500/20 text-blue-400">{campaignTypeLabels[campaign.campaign_type]}</Badge>
                    </div>
                    <p className="text-sm text-slate-400 mb-3">{campaign.advertiser_name}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-slate-400">
                        <Calendar className="w-4 h-4" />
                        <span>{campaign.schedule?.start_date} - {campaign.schedule?.end_date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400">
                        <Target className="w-4 h-4" />
                        <span>{campaign.target?.target_type}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400">
                        <DollarSign className="w-4 h-4" />
                        <span>£{campaign.pricing?.flat_rate || campaign.pricing?.total_budget || 0}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400">
                        <Eye className="w-4 h-4" />
                        <span>{campaign.metrics?.impressions_delivered || 0} impressions</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {campaign.status === "pending_approval" && (
                      <>
                        <Button 
                          size="sm" 
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => { setSelectedCampaign(campaign); setIsApproveOpen(true); }}
                        >
                          <Check className="w-4 h-4 mr-1" />Approve
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="border-red-500 text-red-400 hover:bg-red-500/20"
                          onClick={() => { setSelectedCampaign(campaign); handleReject("Rejected by admin"); }}
                        >
                          <X className="w-4 h-4 mr-1" />Reject
                        </Button>
                      </>
                    )}
                    {campaign.status === "approved" && (
                      <Button 
                        size="sm" 
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleActivate(campaign.campaign_id)}
                      >
                        <Play className="w-4 h-4 mr-1" />Activate
                      </Button>
                    )}
                    {campaign.status === "active" && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-orange-500 text-orange-400 hover:bg-orange-500/20"
                        onClick={() => handlePause(campaign.campaign_id)}
                      >
                        <Pause className="w-4 h-4 mr-1" />Pause
                      </Button>
                    )}
                    {campaign.status === "paused" && (
                      <Button 
                        size="sm" 
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleActivate(campaign.campaign_id)}
                      >
                        <Play className="w-4 h-4 mr-1" />Resume
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {campaigns.length === 0 && !loading && (
            <div className="text-center py-12 text-slate-400">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No campaigns found</p>
            </div>
          )}
        </div>
      </main>

      {/* Approve Dialog */}
      <Dialog open={isApproveOpen} onOpenChange={setIsApproveOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Approve Campaign</DialogTitle>
          </DialogHeader>
          <p className="text-slate-400">Are you sure you want to approve "{selectedCampaign?.name}"?</p>
          <div className="flex gap-3 mt-4">
            <Button onClick={handleApprove} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
              Approve Campaign
            </Button>
            <Button variant="outline" onClick={() => setIsApproveOpen(false)} className="flex-1">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
